import React, { useEffect, useState } from "react";
import axios from "axios";
import { Save, RefreshCw, Image, Type, Video, Plus, Trash2, X } from "lucide-react";

const ManageBannerSection = () => {
  const [formData, setFormData] = useState({
    isActive: true,
    backgroundImage: "",
    subTitle: "",
    mainTitle: "",
    description: "",
    mainVideoId: "",
    featuredVideos: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  // Fetch Data
  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get("https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/banner-section");
      setFormData(res.data.data);
    } catch (error) {
      setMessage({ text: "Error loading data", type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle Input Change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle Featured Videos Changes
  const handleFeaturedChange = (index, field, value) => {
    const updatedVideos = [...formData.featuredVideos];
    updatedVideos[index][field] = value;
    setFormData((prev) => ({ ...prev, featuredVideos: updatedVideos }));
  };

  const addFeaturedVideo = () => {
    setFormData((prev) => ({
      ...prev,
      featuredVideos: [
        ...prev.featuredVideos,
        { id: "", title: "New Video", thumbnail: "" },
      ],
    }));
  };

  const removeFeaturedVideo = (index) => {
    setFormData((prev) => ({
      ...prev,
      featuredVideos: prev.featuredVideos.filter((_, i) => i !== index),
    }));
  };

  // Submit Data
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put("https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/banner-section", formData);
      setMessage({ text: "Banner updated successfully!", type: "success" });
    } catch (error) {
      setMessage({ text: "Failed to update banner.", type: "danger" });
    } finally {
      setSaving(false);
    }
  };

  // Reset Data
  const handleReset = async () => {
    if (window.confirm("Are you sure you want to reset to default settings?")) {
      try {
        await axios.post("https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/banner-section/reset");
        fetchData();
        setMessage({ text: "Reset to defaults.", type: "info" });
      } catch (error) {
        setMessage({ text: "Reset failed.", type: "danger" });
      }
    }
  };

  if (loading) return <div className="text-center mt-5">Loading Admin Panel...</div>;

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold"><Video className="me-2" size={28} /> Manage Banner Section</h2>
        <button className="btn btn-outline-secondary" onClick={handleReset}>
          <RefreshCw size={18} className="me-1" /> Reset Defaults
        </button>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage({ text: "", type: "" })}></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        
        {/* Visual Settings */}
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card shadow-sm">
              <div className="card-header bg-dark fw-bold"><Image className="me-2" size={20}/> Visuals</div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label text-dark">Background Image URL</label>
                  <input
                    type="text" className="form-control" name="backgroundImage"
                    value={formData.backgroundImage} onChange={handleChange}
                    placeholder="https://..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Text Content */}
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card shadow-sm">
              <div className="card-header bg-dark fw-bold"><Type className="me-2" size={20}/> Content</div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label text-dark">Subtitle (Small text)</label>
                  <input type="text" className="form-control" name="subTitle" value={formData.subTitle} onChange={handleChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label text-dark">Main Title</label>
                  <textarea className="form-control" name="mainTitle" rows="2" value={formData.mainTitle} onChange={handleChange}></textarea>
                </div>
                <div className="mb-3">
                  <label className="form-label text-dark">Description</label>
                  <textarea className="form-control" name="description" rows="3" value={formData.description} onChange={handleChange}></textarea>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Video */}
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card shadow-sm">
              <div className="card-header bg-dark fw-bold"><Video className="me-2" size={20}/> Main Video</div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label text-dark">YouTube Video ID</label>
                  <div className="input-group">
                    <span className="input-group-text">https://youtube.com/watch?v=</span>
                    <input type="text" className="form-control" name="mainVideoId" value={formData.mainVideoId} onChange={handleChange} placeholder="g5eQgEuiFC8" />
                  </div>
                  <div className="form-text">Example: For url youtube.com/watch?v=<strong>g5eQgEuiFC8</strong>, enter <strong>g5eQgEuiFC8</strong></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Featured Videos */}
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card shadow-sm">
              <div className="card-header bg-white d-flex justify-content-between align-items-center fw-bold">
                <span><Video className="me-2" size={20}/> Featured Videos (Thumbnails)</span>
                <button type="button" className="btn btn-sm btn-primary" onClick={addFeaturedVideo}>
                  <Plus size={16} className="me-1"/> Add Video
                </button>
              </div>
              <div className="card-body">
                {formData.featuredVideos.map((video, index) => (
                  <div key={index} className="card bg-light mb-3 p-3 border-0">
                    <div className="d-flex justify-content-between mb-2">
                      <strong>Video #{index + 1}</strong>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeFeaturedVideo(index)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="row g-2">
                      <div className="col-md-4">
                        <label className="small text-muted">YouTube ID</label>
                        <input type="text" className="form-control form-control-sm" value={video.id} onChange={(e) => handleFeaturedChange(index, "id", e.target.value)} placeholder="ID" />
                      </div>
                      <div className="col-md-4">
                        <label className="small text-muted">Title</label>
                        <input type="text" className="form-control form-control-sm" value={video.title} onChange={(e) => handleFeaturedChange(index, "title", e.target.value)} placeholder="Title" />
                      </div>
                      <div className="col-md-4">
                        <label className="small text-muted">Thumbnail URL (Optional)</label>
                        <input type="text" className="form-control form-control-sm" value={video.thumbnail} onChange={(e) => handleFeaturedChange(index, "thumbnail", e.target.value)} placeholder="Image URL" />
                      </div>
                    </div>
                  </div>
                ))}
                {formData.featuredVideos.length === 0 && <p className="text-muted text-center">No featured videos added.</p>}
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button type="submit" className="btn btn-success px-4" disabled={saving}>
            {saving ? "Saving..." : <><Save size={18} className="me-2" /> Save Changes</>}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ManageBannerSection;